import React, { useMemo } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { MapChart } from "echarts/charts";
import { TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { useChartTheme } from "../charts/theme";
import europe from "./europe.map.json";

/**
 * The market league table as a choropleth.
 *
 * echarts, like every other chart here, so it inherits the same theme, tooltip
 * and canvas renderer. echarts ships no map data, so the outlines are vendored
 * by scripts/build-europe-map.mjs from Natural Earth 1:110m; echarts projects
 * them.
 *
 * Countries with no market are drawn unfilled. Four coloured shapes floating in
 * space read as abstract art; the same four against their neighbours read as a
 * map.
 */

echarts.use([TooltipComponent, MapChart, CanvasRenderer]);

const MAP_NAME = "healthDashboardEurope";
echarts.registerMap(MAP_NAME, europe);

/** Health bands: green above 90, amber above 80, red below. */
export const healthBand = (percent) => {
  if (percent === null || percent === undefined || Number.isNaN(percent)) {
    return "unknown";
  }
  if (percent > 90) return "good";
  if (percent > 80) return "warn";
  return "bad";
};

const BAND_COLOUR = {
  good: "#16a34a",
  warn: "#d97706",
  bad: "#dc2626",
  unknown: "#6b7280",
};

const BAND_LABEL = {
  good: "above 90%",
  warn: "80–90%",
  bad: "below 80%",
  unknown: "no reading",
};

/** Country name by market code, from the vendored file rather than a second list. */
const COUNTRY_BY_MARKET = new Map(
  europe.features
    .filter((f) => f.properties.market)
    .map((f) => [f.properties.market, f.properties.name])
);

export default function MarketsMap({ markets, onMarketClick }) {
  const theme = useChartTheme();

  const option = useMemo(() => {
    // Only markets that have an outline can be drawn. Any other market still
    // appears as a chip below, so it is never silently dropped.
    const byCountry = new Map(
      markets
        .filter((m) => COUNTRY_BY_MARKET.has(m.market))
        .map((m) => [COUNTRY_BY_MARKET.get(m.market), m])
    );

    /**
     * Every country gets an entry, not just the markets.
     *
     * Hovering a neighbour used to produce a tooltip reading "undefined":
     * echarts falls back to its default formatting for a region the series has
     * no data for. An entry with no market lets the formatter return an empty
     * string, which echarts treats as "hide".
     *
     * Note per-item `tooltip: { show: false }` does *not* work here — echarts
     * then leaves the previous tooltip on screen, so hovering the UK and moving
     * to France kept claiming France was the UK.
     */
    const data = europe.features.map((feature) => {
      const { name } = feature.properties;
      const market = byCountry.get(name);

      if (!market) {
        return {
          name,
          value: null,
          itemStyle: { areaColor: theme.track },
        };
      }

      const band = healthBand(market.healthPercent);
      return {
        name,
        value: market.healthPercent ?? null,
        market: market.market,
        band,
        stores: market.stores,
        // Carried through so the tooltip can qualify the count the same way
        // the table does, rather than stating a total the table flags as
        // unverified.
        suspect: market.suspect,
        itemStyle: { areaColor: BAND_COLOUR[band] },
      };
    });

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        backgroundColor: theme.tooltipBg,
        borderColor: theme.split,
        textStyle: { color: theme.text },
        formatter: (params) => {
          const { market, band, value, stores, suspect } = params.data || {};
          // Empty string is how a tooltip is suppressed: the neighbours are
          // context, and have nothing to report.
          if (!market) return "";
          const reading = value === null ? "—" : `${value}%`;
          const count =
            stores === null || stores === undefined
              ? "—"
              : `${stores.toLocaleString()}${suspect ? " (unverified)" : ""}`;
          return [
            `<strong>${market}</strong> · ${params.name}`,
            `Health ${reading} (${BAND_LABEL[band]})`,
            `Stores ${count}`,
          ].join("<br/>");
        },
      },
      series: [
        {
          type: "map",
          map: MAP_NAME,

          // Use the whole panel. Without these the series picks its own much
          // smaller box and sits in the middle of the card however the card is
          // sized.
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,

          /**
           * The frame to fit, as lon/lat corners.
           *
           * This is the piece that was missing. Left to derive its own bounds
           * from the shapes, echarts lays them out on its own terms and the map
           * ends up a fixed rectangle adrift in the panel. It is the same
           * rectangle the outlines were clipped to, so the data fills it.
           *
           * On the geo *component* the colours would have to move to
           * `geo.regions` — a map series ignores series-data itemStyle when it
           * renders through geoIndex, which drew every country grey.
           */
          boundingCoords: [
            [europe.view.west, europe.view.north],
            [europe.view.east, europe.view.south],
          ],

          // Fit the container and let the reader zoom and pan from there rather
          // than the app fixing a framing. This captures the wheel over the map,
          // so the page will not scroll while the pointer is on it.
          roam: true,
          scaleLimit: { min: 1, max: 8 },

          // echarts lays lon/lat out as plain x/y, so x is narrowed by
          // cos(mid-latitude) or the map looks stretched sideways. Generated
          // with the outlines so the two cannot disagree.
          aspectScale: europe.view.aspectScale,
          selectedMode: false,
          label: { show: false },
          // Regions with no market fall through to this: an outline, no fill.
          itemStyle: {
            areaColor: theme.track,
            borderColor: theme.tooltipBg,
            borderWidth: 0.8,
          },
          emphasis: {
            label: { show: false },
            itemStyle: { areaColor: null, borderColor: theme.text, borderWidth: 1.5 },
          },
          data,
        },
      ],
    };
  }, [markets, theme]);

  const onEvents = useMemo(
    () => ({
      click: (params) => {
        if (params?.data?.market) onMarketClick?.(params.data.market);
      },
    }),
    [onMarketClick]
  );

  return (
    <div className="markets-map">
      {/* Fills the space the card already has, rather than claiming its own.
          Sizing it to the map made the panel grow taller than the chart beside
          it, so the whole row jumped when the tab changed. */}
      <div className="markets-map__canvas">
        <ReactEChartsCore
          echarts={echarts}
          option={option}
          onEvents={onEvents}
          // echarts-for-react defaults the container to 300px, which would
          // otherwise win over the flexed height.
          style={{ width: "100%", height: "100%" }}
          opts={{ renderer: "canvas" }}
          notMerge
        />
      </div>

      {/* Bands are named here as well as coloured on the map: red and green
          alone are indistinguishable to the most common colour blindness. The
          chips are also the only way to reach a market with no outline. */}
      <ul className="markets-map__legend">
        {markets.map((market) => {
          const band = healthBand(market.healthPercent);
          return (
            <li key={market.market}>
              <button
                type="button"
                className="markets-map__chip"
                onClick={() => onMarketClick?.(market.market)}
                title={`${market.market} — health ${market.health} (${BAND_LABEL[band]})`}
              >
                <span
                  className="markets-map__swatch"
                  style={{ background: BAND_COLOUR[band] }}
                />
                <span className="markets-map__code">{market.market}</span>
                <span className="markets-map__value">{market.health}</span>
                {COUNTRY_BY_MARKET.has(market.market) ? null : (
                  <span className="markets-map__unmapped" title="No outline for this market">
                    ·
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
